import { useCallback, useMemo, useState } from 'react'

import { getFractionImproperToMixedChooseQuestionData, FractionModel } from './FractionImproperToMixedChoose.utils'

interface UseFractionImproperToMixedChooseReturnType {
  question?: FractionModel
  options?: FractionModel[]
  formValid: boolean
  selectedAnswer?: number
  handleCheckAnswer: () => void
  onInputChange: (val?: number) => void
  goToNextQuestion: () => void
}

interface UseProperFractionProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
}

function useFractionImproperToMixedChoose({
  difficultyLevel,
  onAnswerStateChange,
  onNextQuestionClick,
}: UseProperFractionProps): UseFractionImproperToMixedChooseReturnType {
  // eslint-disable-next-line
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const {
    correctAnswerIndex = -1,
    question,
    options,
    maxVariation,
  } = useMemo(
    () => getFractionImproperToMixedChooseQuestionData(difficultyLevel, taskHistory),
    [difficultyLevel, taskHistory],
  )
  const [selectedAnswer, setSelectedAnswer] = useState<number>()

  const handleCheckAnswer = useCallback((): void => {
    if (selectedAnswer === undefined) return
    onAnswerStateChange(selectedAnswer === correctAnswerIndex)
  }, [selectedAnswer, onAnswerStateChange, correctAnswerIndex])

  const onInputChange = useCallback(
    (val?: number) => {
      setSelectedAnswer(val)
      onAnswerStateChange(null)
    },
    [onAnswerStateChange],
  )

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) =>
      [`${question.numerator}-${question.denominator}`, ...prevState].slice(0, maxVariation * 0.7),
    )
    onInputChange()
    onNextQuestionClick()
  }, [onInputChange, onNextQuestionClick, maxVariation, question])

  return {
    question,
    options,
    formValid: selectedAnswer !== undefined,
    selectedAnswer,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
  }
}

export default useFractionImproperToMixedChoose
